import { useCallback, useEffect, useRef, useState } from 'react';

export type GeoStatus = 'unsupported' | 'idle' | 'requesting' | 'granted' | 'denied' | 'error';

export interface GeoCoords {
  lat: number;
  lng: number;
  accuracy: number;
  /** UTC ISO timestamp when the position was sampled. */
  capturedAt: string;
}

interface GeoState {
  status: GeoStatus;
  coords: GeoCoords | null;
  errorMessage: string | null;
}

interface UseGeolocationOptions {
  /** Auto-request on mount. Default true. */
  autoRequest?: boolean;
  /** GeolocationPositionOptions passed to `getCurrentPosition`. */
  enableHighAccuracy?: boolean;
  timeoutMs?: number;
  maximumAgeMs?: number;
}

/**
 * Browser-geolocation hook with explicit lifecycle states.
 *
 * The clock-in/out feature *requires* GPS (per project decision: mode B).
 * This hook surfaces every failure mode the UI must handle:
 *   - unsupported : browser has no Geolocation API
 *   - denied      : user clicked "Block" on the permission prompt
 *   - error       : timeout or unknown failure
 *
 * Always call `refresh()` immediately before submitting a clock event so
 * the captured coords are fresh (not from page load 30 minutes ago).
 */
export function useGeolocation(opts: UseGeolocationOptions = {}): {
  status: GeoStatus;
  coords: GeoCoords | null;
  errorMessage: string | null;
  refresh: () => Promise<GeoCoords | null>;
} {
  const { autoRequest = true, enableHighAccuracy = true, timeoutMs = 15_000, maximumAgeMs = 0 } = opts;

  const supported = typeof navigator !== 'undefined' && 'geolocation' in navigator;
  const [state, setState] = useState<GeoState>(() => ({
    status: supported ? 'idle' : 'unsupported',
    coords: null,
    errorMessage: supported ? null : 'Geolocation is not supported on this device.',
  }));

  // Keep the latest options in a ref so refresh() doesn't need to depend on them.
  const optsRef = useRef({ enableHighAccuracy, timeoutMs, maximumAgeMs });
  optsRef.current = { enableHighAccuracy, timeoutMs, maximumAgeMs };

  const refresh = useCallback(async (): Promise<GeoCoords | null> => {
    if (!supported) return null;
    setState((s) => ({ ...s, status: 'requesting', errorMessage: null }));
    return new Promise<GeoCoords | null>((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const coords: GeoCoords = {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
            capturedAt: new Date(pos.timestamp).toISOString(),
          };
          setState({ status: 'granted', coords, errorMessage: null });
          resolve(coords);
        },
        (geoErr) => {
          if (geoErr.code === geoErr.PERMISSION_DENIED) {
            setState({
              status: 'denied',
              coords: null,
              errorMessage:
                'Location access denied. Enable it in your browser settings to clock in/out.',
            });
          } else if (geoErr.code === geoErr.POSITION_UNAVAILABLE) {
            setState({
              status: 'error',
              coords: null,
              errorMessage: 'Location unavailable. Check your device GPS / network.',
            });
          } else if (geoErr.code === geoErr.TIMEOUT) {
            setState({
              status: 'error',
              coords: null,
              errorMessage: 'Location request timed out. Please try again.',
            });
          } else {
            setState({
              status: 'error',
              coords: null,
              errorMessage: geoErr.message || 'Unknown geolocation error.',
            });
          }
          resolve(null);
        },
        {
          enableHighAccuracy: optsRef.current.enableHighAccuracy,
          timeout: optsRef.current.timeoutMs,
          maximumAge: optsRef.current.maximumAgeMs,
        },
      );
    });
  }, [supported]);

  useEffect(() => {
    if (autoRequest && supported) {
      void refresh();
    }
    // We intentionally only auto-request once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    status: state.status,
    coords: state.coords,
    errorMessage: state.errorMessage,
    refresh,
  };
}
